import { NgZone } from '@angular/core';
import { httpsCallable } from 'firebase/functions';
import { firebaseFunctions } from '../../firebase/firebase-init';
import { Observable, timer } from 'rxjs';
import { map, shareReplay, switchMap, filter } from 'rxjs/operators';
import {
  ActivePickup,
  Announcement,
  CameraWatcher,
  ChildPresence,
  ReadyBoardState,
  ScheduleItem
} from './ready-board.models';

export class FirestoreReadyBoardDataSource {
  readonly state$: Observable<ReadyBoardState>;

  // Created once — avoids allocating a new callable reference on every 5s poll tick.
  private readonly getStateFn = httpsCallable<void, FullReadyBoardStateResponse>(
    firebaseFunctions, 'internalReadyBoardGetState'
  );

  constructor(private ngZone: NgZone) {
    // Run the polling timer outside Angular's Zone so Zone.js doesn't track the
    // repeating interval as a pending task — this prevents the "Application did
    // not stabilize within 9 seconds" warning. Results are brought back into the
    // zone via ngZone.run() so change detection fires normally.
    this.state$ = new Observable<ReadyBoardState>(observer => {
      const sub = ngZone.runOutsideAngular(() =>
        timer(0, 5000).pipe(
          switchMap(async () => {
            try {
              const result = await this.getStateFn();
              return this.mapResponseToState(result.data);
            } catch (e) {
              console.error('[ReadyBoard] Failed to fetch state via function. Retrying in next tick.', e);
              return null;
            }
          }),
          map(result => result as ReadyBoardState),
          filter(result => !!result),
        ).subscribe({
          next: state => ngZone.run(() => observer.next(state)),
          error: e => ngZone.run(() => observer.error(e)),
          complete: () => ngZone.run(() => observer.complete()),
        })
      );
      return () => sub.unsubscribe();
    }).pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private mapResponseToState(data: FullReadyBoardStateResponse): ReadyBoardState {
    const now = Date.now();
    const activePickups = (data.activePickups || []).map(p => ({
      ...p,
      updatedAt: p.updatedAt ? new Date(p.updatedAt) : new Date(),
      expiresAt: p.expiresAt ? new Date(p.expiresAt) : undefined
    } as ActivePickup))
      .filter(p => {
        if (!p.expiresAt) return true;
        const ms = p.expiresAt instanceof Date ? p.expiresAt.getTime() : p.expiresAt.toMillis();
        return ms > now;
      })
      .sort((a, b) => {
        const pa = a.status === 'READY_PICKUP' ? 0 : 1;
        const pb = b.status === 'READY_PICKUP' ? 0 : 1;
        return pa - pb;
      });

    const announcements = (data.announcements || []).map(a => ({
      ...a,
      active: true
    } as Announcement));

    const internalAnnouncements = (data.internalAnnouncements || []).map(a => ({
      ...a,
      active: true
    } as Announcement));

    const presentChildren = (data.presentChildren || []).map(child => ({
      ...child,
      cameraWatchers: (child.cameraWatchers || []).map(w => ({
        ...w,
        startedAt: w.startedAt ? new Date(w.startedAt) : undefined
      } as CameraWatcher))
    } as ChildPresence));

    return {
      activePickups,
      announcements,
      schedule: data.schedule || [],
      tickerText: data.tickerText || '',
      internalAnnouncements,
      presentChildren
    };
  }

  private getEmptyState(): ReadyBoardState {
    return {
      activePickups: [],
      announcements: [],
      schedule: [],
      tickerText: '',
      internalAnnouncements: [],
      presentChildren: []
    };
  }
}

type FullReadyBoardStateResponse = {
  activePickups: { childDisplayName: string; childId?: string; status: ActivePickup['status']; gate?: string; updatedAt?: number; expiresAt?: number }[];
  announcements: { text: string; type: Announcement['type']; validFrom?: number; validTo?: number }[];
  schedule: ScheduleItem[];
  tickerText: string;
  internalAnnouncements: { text: string; type: Announcement['type']; validFrom?: number; validTo?: number }[];
  presentChildren: (Omit<ChildPresence, 'cameraWatchers'> & {
    cameraWatchers?: (Omit<CameraWatcher, 'startedAt'> & { startedAt?: number })[];
  })[];
};
